import { AuthProvider, useAuth } from './lib/auth'
import { isFirebaseConfigured } from './lib/env'
import { useRoute, type Route } from './lib/router'
import { useIsDesktop } from './lib/media'
import { WorkspaceProvider, useWorkspace } from './data/workspace'
import { Header } from './ui/Header'
import { TabBar } from './ui/TabBar'
import { Sidebar } from './ui/Sidebar'
import { SetupScreen } from './ui/SetupScreen'
import { SignInScreen, Wordmark } from './ui/SignInScreen'
import { useTheme } from './ui/useTheme'
import { NowPage } from './pages/NowPage'
import { SettingsPage } from './pages/SettingsPage'
import { TodayPage } from './pages/TodayPage'
import { WeekPage } from './pages/WeekPage'
import { InsightsPage } from './pages/InsightsPage'
import { IterationsPage } from './pages/IterationsPage'
import { TrackPage } from './pages/TrackPage'
import { formatDayLabel } from './lib/format'
import { todayKey } from './lib/time'

export function App() {
  if (!isFirebaseConfigured) return <SetupScreen />
  return (
    <AuthProvider>
      <AuthGate />
    </AuthProvider>
  )
}

function AuthGate() {
  const { status, uid } = useAuth()
  if (status === 'loading') {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Wordmark />
      </div>
    )
  }
  if (status === 'signed-out' || !uid) return <SignInScreen />
  return (
    <WorkspaceProvider uid={uid}>
      <Shell />
    </WorkspaceProvider>
  )
}

const TITLES: Record<Route, string> = {
  now: 'Now',
  today: 'Today',
  week: 'Week',
  track: 'Trackers',
  iterations: 'Iterations',
  insights: 'Insights',
  settings: 'Settings',
}

/**
 * Two genuinely different layouts rather than one stretched. A phone gets a
 * bottom tab bar and one column; a desktop gets a persistent sidebar and the
 * full width, because a week of seven columns needs it.
 */
function Shell() {
  const route = useRoute()
  const desktop = useIsDesktop()
  const { settings, error } = useWorkspace()
  useTheme(settings.theme)

  const page = <Page route={route} />

  if (desktop) {
    return (
      <div className="flex h-dvh overflow-hidden">
        <Sidebar current={route} />
        <div className="flex min-w-0 min-h-0 flex-1 flex-col">
          <div className="flex h-14 shrink-0 items-baseline gap-3 border-b border-line px-4">
            <h1 className="text-[19px] font-semibold tracking-tight">{TITLES[route]}</h1>
            <p className="text-[12px] text-faint tnum">
              {formatDayLabel(todayKey(settings.dayStartHour), 'EEEE d MMMM')}
            </p>
          </div>
          {error ? <p className="px-4 pt-3 text-[13px] text-danger">{error}</p> : null}
          <div className="flex min-h-0 flex-1 flex-col">{page}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden">
      <Header title={TITLES[route]} subtitle={formatDayLabel(todayKey(settings.dayStartHour))} />
      {error ? (
        <p className="mx-auto w-full max-w-lg px-4 pt-3 text-[13px] text-danger">{error}</p>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col">{page}</div>
      <TabBar current={route} />
    </div>
  )
}

function Page({ route }: { route: Route }) {
  switch (route) {
    case 'today':
      return <TodayPage />
    case 'week':
      return <WeekPage />
    case 'track':
      return <TrackPage />
    case 'iterations':
      return <IterationsPage />
    case 'insights':
      return <InsightsPage />
    case 'settings':
      return <SettingsPage />
    default:
      return <NowPage />
  }
}
